import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import { AppHeader } from "@/components/layout/AppHeader";
import {
  TISACard,
  BookCard,
  AchievementCard,
} from "@/components/tisa/TISACard";
import { TISAButton } from "@/components/tisa/TISAButton";
import {
  getUserLoans,
  getUserCoinBalance,
  getCoinTransactions,
  getBooks,
} from "@/lib/supabase";
import {
  getAgeAppropriateGreeting,
  getDaysUntilDue,
  getDueDateStatus,
} from "@/lib/utils";
import { IB_PYP_THEMES } from "@/types/database";
import { Book, Clock, Trophy, Coins, Star, BookOpen } from "lucide-react";

export function StudentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Fetch user's current loans
  const { data: loans = [], isLoading: loansLoading } = useQuery({
    queryKey: ["user-loans", user?.id],
    queryFn: () => getUserLoans(user!.id),
    enabled: !!user?.id,
    staleTime: 0, // Always refetch for fresh data
  });

  // Fetch user's coin balance
  const { data: coinBalance = 0, isLoading: coinsLoading } = useQuery({
    queryKey: ["user-coins", user?.id],
    queryFn: () => getUserCoinBalance(user!.id),
    enabled: !!user?.id,
  });

  // Fetch recent coin transactions
  const { data: transactions = [] } = useQuery({
    queryKey: ["coin-transactions", user?.id],
    queryFn: () => getCoinTransactions(user!.id, 5),
    enabled: !!user?.id,
  });

  // Fetch recommended books (books user hasn't borrowed)
  const { data: recommendedBooks = [] } = useQuery({
    queryKey: ["recommended-books"],
    queryFn: () => getBooks({ available_only: true }),
    select: (books) => books.slice(0, 6), // Show 6 recommendations
  });

  if (!user) {
    return (
      <div className="text-center">Please log in to see your dashboard</div>
    );
  }

  // Calculate reading progress for rainbow challenge
  const borrowedThemes = loans
    .map((loan) => loan.copy?.book?.theme_color)
    .filter(Boolean);
  const uniqueThemes = [...new Set(borrowedThemes)];
  const rainbowProgress = (uniqueThemes.length / 6) * 100;

  // Simple achievements based on current data
  const achievements = [
    {
      id: "first-book",
      title: "First Book!",
      description: "Borrowed your first book",
      icon: "📖",
      unlocked: loans.length > 0,
      coinReward: 10,
    },
    {
      id: "coin-collector",
      title: "Coin Collector",
      description: "Earned 20+ TISAcoins",
      icon: "🪙",
      unlocked: (coinBalance ?? 0) >= 20,
      coinReward: 5,
    },
    {
      id: "rainbow-reader",
      title: "Rainbow Reader",
      description: "Read from all 6 IB themes",
      icon: "🌈",
      unlocked: uniqueThemes.length === 6,
      coinReward: 50,
    },
    {
      id: "book-lover",
      title: "Book Lover",
      description: "Borrowed 5+ books",
      icon: "❤️",
      unlocked: loans.length >= 5,
      coinReward: 25,
    },
  ];

  return (
    <>
      <AppHeader />

      <main className="p-4 sm:p-6 lg:p-8">
        <div className="space-y-8 max-w-7xl mx-auto">
          {/* Welcome Header */}
          <div className="text-center space-y-4">
            <h1 className="text-child-4xl font-bold text-primary">
              {getAgeAppropriateGreeting(user.name)}
            </h1>
            <p className="text-child-lg text-muted-foreground">
              Ready for your next reading adventure? 📚✨
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <TISACard
              themeColor="yellow"
              className="text-center"
              header={
                <div className="flex items-center gap-2">
                  <Coins className="h-6 w-6 text-tisa-yellow-600" />
                  <span className="font-semibold text-child-lg">TISAcoins</span>
                </div>
              }
            >
              <div className="space-y-3">
                <div className="text-child-3xl font-bold text-tisa-yellow-600">
                  {coinsLoading ? "..." : coinBalance}
                </div>
                <div className="tisacoin">🪙 Your Balance</div>
              </div>
            </TISACard>

            <TISACard
              themeColor="blue"
              className="text-center"
              header={
                <div className="flex items-center gap-2">
                  <BookOpen className="h-6 w-6 text-tisa-blue-600" />
                  <span className="font-semibold text-child-lg">My Books</span>
                </div>
              }
            >
              <div className="space-y-3">
                <div className="text-child-3xl font-bold text-tisa-blue-600">
                  {loansLoading ? "..." : loans.length}
                </div>
                <p className="text-child-sm text-muted-foreground">
                  Currently borrowed
                </p>
              </div>
            </TISACard>

            <TISACard
              themeColor="purple"
              className="text-center"
              header={
                <div className="flex items-center gap-2">
                  <Star className="h-6 w-6 text-tisa-purple-600" />
                  <span className="font-semibold text-child-lg">Progress</span>
                </div>
              }
            >
              <div className="space-y-3">
                <div className="text-child-3xl font-bold text-tisa-purple-600">
                  {uniqueThemes.length}/6
                </div>
                <p className="text-child-sm text-muted-foreground">
                  Rainbow themes read
                </p>
              </div>
            </TISACard>
          </div>

          {/* Rainbow Reading Challenge */}
          <TISACard
            className="p-6"
            header={
              <div className="flex items-center gap-3">
                <div className="text-2xl">🌈</div>
                <div>
                  <h3 className="text-child-xl font-bold">
                    Rainbow Reading Challenge
                  </h3>
                  <p className="text-child-sm text-muted-foreground">
                    Read books from all 6 IB PYP themes to complete the rainbow!
                  </p>
                </div>
              </div>
            }
          >
            <div className="space-y-4">
              <div className="progress-rainbow rounded-full">
                <div
                  className="h-full bg-gradient-to-r from-tisa-red-500 to-tisa-purple-500 rounded-full transition-all duration-500"
                  style={{ width: `${rainbowProgress}%` }}
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {Object.entries(IB_PYP_THEMES).map(([color, theme]) => {
                  const completed = uniqueThemes.includes(color as any);
                  return (
                    <div
                      key={color}
                      className={`theme-${color} p-3 rounded-lg text-center transition-all duration-300 ${
                        completed
                          ? "ring-2 ring-offset-2 ring-current shadow-lg"
                          : "opacity-60"
                      }`}
                    >
                      <div className="text-2xl mb-1">{theme.emoji}</div>
                      <div className="text-child-xs font-medium">
                        {theme.name}
                      </div>
                      {completed && <div className="text-lg mt-1">✅</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          </TISACard>

          {/* Current Books */}
          {loans.length > 0 && (
            <TISACard
              header={
                <div className="flex items-center gap-3">
                  <Book className="h-6 w-6 text-primary" />
                  <h3 className="text-child-xl font-bold">My Current Books</h3>
                </div>
              }
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {loans.map((loan) => {
                  const book = loan.copy?.book;
                  if (!book) return null;

                  const daysUntilDue = getDaysUntilDue(new Date(loan.due_at));
                  const dueDateStatus = getDueDateStatus(new Date(loan.due_at));

                  return (
                    <div key={loan.id} className="relative">
                      <BookCard
                        title={book.title}
                        author={book.author}
                        theme={book.theme_color}
                        coverUrl={book.cover_url ?? undefined}
                        available={true}
                      />

                      {/* Due date indicator */}
                      <div
                        className={`absolute top-2 right-2 px-2 py-1 rounded-full text-child-xs font-medium ${
                          dueDateStatus === "overdue"
                            ? "bg-destructive text-destructive-foreground"
                            : dueDateStatus === "due-soon"
                              ? "bg-warning text-warning-foreground"
                              : dueDateStatus === "warning"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-green-100 text-green-800"
                        }`}
                      >
                        <Clock className="h-3 w-3 inline mr-1" />
                        {daysUntilDue > 0
                          ? `${daysUntilDue}d left`
                          : "Due today!"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </TISACard>
          )}

          {/* Achievements */}
          <TISACard
            header={
              <div className="flex items-center gap-3">
                <Trophy className="h-6 w-6 text-tisa-yellow-600" />
                <h3 className="text-child-xl font-bold">Your Achievements</h3>
              </div>
            }
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {achievements.map((achievement) => (
                <AchievementCard
                  key={achievement.id}
                  title={achievement.title}
                  description={achievement.description}
                  icon={achievement.icon}
                  unlocked={achievement.unlocked}
                  coinReward={achievement.coinReward}
                />
              ))}
            </div>
          </TISACard>

          {/* Recommended Books */}
          {recommendedBooks.length > 0 && (
            <TISACard
              header={
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Star className="h-6 w-6 text-tisa-green-600" />
                    <h3 className="text-child-xl font-bold">
                      Books You Might Love
                    </h3>
                  </div>
                  <TISAButton
                    variant="outline"
                    size="sm"
                    onClick={() => navigate("/student/books")}
                  >
                    See All Books
                  </TISAButton>
                </div>
              }
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {recommendedBooks.map((book) => (
                  <BookCard
                    key={book.id}
                    title={book.title}
                    author={book.author}
                    theme={book.theme_color}
                    coverUrl={book.cover_url ?? undefined}
                    available={book.copies?.some(
                      (copy) => copy.status === "available",
                    )}
                  />
                ))}
              </div>
            </TISACard>
          )}

          {/* Recent Coin Activity */}
          {transactions.length > 0 && (
            <TISACard
              header={
                <div className="flex items-center gap-3">
                  <Coins className="h-6 w-6 text-tisa-yellow-600" />
                  <h3 className="text-child-xl font-bold">
                    Recent Coin Activity
                  </h3>
                </div>
              }
            >
              <div className="space-y-3">
                {transactions.map((transaction) => (
                  <div
                    key={transaction.id}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      transaction.delta > 0 ? "bg-green-50" : "bg-red-50"
                    }`}
                  >
                    <div>
                      <p className="text-child-sm font-medium">
                        {transaction.note}
                      </p>
                      <p className="text-child-xs text-muted-foreground">
                        {transaction.created_at
                          ? new Date(
                              transaction.created_at,
                            ).toLocaleDateString()
                          : ""}
                      </p>
                    </div>
                    <div
                      className={`text-child-base font-bold ${
                        transaction.delta > 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {transaction.delta > 0 ? "+" : ""}
                      {transaction.delta} 🪙
                    </div>
                  </div>
                ))}
              </div>
            </TISACard>
          )}
        </div>
      </main>
    </>
  );
}
